import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import VenueTab from "./Venue/VenueTab";
import EventTab from "./Event/EventTab";
import PerformerTab from "./PerformerTab";
import ComposerTab from "./ComposerTab";
import HotelTab from "./HotelTab";
import AdminUserTab from "./AdminUserTab";
import InformationTab from "./InformationTab";
import NewsletterTab from "./NewsletterTab";
import { OperaContext } from "../../context/OperaContext";
import ImportTab from "./Import/ImportTab";
import CityTab from "./CityTab";

function Admin() {
  const [activeTab, setActiveTab] = useState("venues");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState("");
  const [authChecked, setAuthChecked] = useState(false);
  const navigate = useNavigate();
  const { fetchAdminData, loading, dataFetched } = useContext(OperaContext);

  useEffect(() => {
    // Check authentication status
    const checkAuth = async () => {
      try {
        // console.log('Checking authentication...');
        const response = await axios.get("/api/auth/check", {
          withCredentials: true,
        });
        setIsAuthenticated(true);
        setUserRole(response.data.role);
        // console.log('Admin: Authenticated successfully');
        await fetchAdminData();
      } catch (err) {
        console.error("Admin: Authentication failed:", err);
        navigate("/admin/login");
      } finally {
        setAuthChecked(true);
      }
    };
    checkAuth();
  }, [navigate, fetchAdminData]);

  const handleLogout = async () => {
    try {
      await axios.post("/api/auth/logout", {}, { withCredentials: true });
      navigate("/admin/login");
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  //show loading spinner if data is being fetched
  if (!authChecked || (isAuthenticated && loading && !dataFetched)) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl text-[#d4a017] font-cinzel uppercase font-normal mb-4">
            Loading Admin Dashboard
          </h1>
          <p className="text-white">Please wait while we load your data...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null; // Render nothing while checking auth
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a] text-white">
      <div className="flex flex-col md:flex-row min-h-[calc(100vh-4rem)] mt-16">
        {/* Sidebar */}
        <div className="w-full md:w-[200px] bg-[#1a1a1a] p-4 md:p-6 md:fixed md:top-20 md:left-0 md:h-[calc(100vh-5rem)] md:border-r md:border-[#333]">
          <h1 className="text-2xl text-[#d4a017] font-cinzel uppercase font-normal text-center md:text-left mb-6">
            Admin Dashboard
          </h1>
          <div className="flex flex-row md:flex-col gap-2">
            <button
              onClick={() => setActiveTab("venues")}
              className={`px-4 py-2 text-white font-light text-base rounded-md transition-colors ${
                activeTab === "venues"
                  ? "bg-[#d4a017]"
                  : "bg-[#333] hover:bg-[#555]"
              }`}
            >
              Venues
            </button>
            <button
              onClick={() => setActiveTab("events")}
              className={`px-4 py-2 text-white font-light text-base rounded-md transition-colors ${
                activeTab === "events"
                  ? "bg-[#d4a017]"
                  : "bg-[#333] hover:bg-[#555]"
              }`}
            >
              Events
            </button>
            <button
              onClick={() => setActiveTab("cities")}
              className={`px-4 py-2 text-white font-light text-base rounded-md transition-colors ${
                activeTab === "cities"
                  ? "bg-[#d4a017]"
                  : "bg-[#333] hover:bg-[#555]"
              }`}
            >
              Cities
            </button>
            <button
              onClick={() => setActiveTab("performers")}
              className={`px-4 py-2 text-white font-light text-base rounded-md transition-colors ${
                activeTab === "performers"
                  ? "bg-[#d4a017]"
                  : "bg-[#333] hover:bg-[#555]"
              }`}
            >
              Artists
            </button>
            <button
              onClick={() => setActiveTab("composers")}
              className={`px-4 py-2 text-white font-light text-base rounded-md transition-colors ${
                activeTab === "composers"
                  ? "bg-[#d4a017]"
                  : "bg-[#333] hover:bg-[#555]"
              }`}
            >
              Composers
            </button>
            <button
              onClick={() => setActiveTab("hotels")}
              className={`px-4 py-2 text-white font-light text-base rounded-md transition-colors ${
                activeTab === "hotels"
                  ? "bg-[#d4a017]"
                  : "bg-[#333] hover:bg-[#555]"
              }`}
            >
              Hotels
            </button>
            <button
              onClick={() => setActiveTab("imports")}
              className={`px-4 py-2 text-white font-light text-base rounded-md transition-colors ${
                activeTab === "imports"
                  ? "bg-[#d4a017]"
                  : "bg-[#333] hover:bg-[#555]"
              }`}
            >
              Imports
            </button>
            <button
              onClick={() => setActiveTab("information")}
              className={`px-4 py-2 text-white font-light text-base rounded-md transition-colors ${
                activeTab === "information"
                  ? "bg-[#d4a017]"
                  : "bg-[#333] hover:bg-[#555]"
              }`}
            >
              Information
            </button>
            <button
              onClick={() => setActiveTab("newsletter")}
              className={`px-4 py-2 text-white font-light text-base rounded-md transition-colors ${
                activeTab === "newsletter"
                  ? "bg-[#d4a017]"
                  : "bg-[#333] hover:bg-[#555]"
              }`}
            >
              Newsletter
            </button>
            {userRole === "superadmin" && (
              <button
                onClick={() => setActiveTab("users")}
                className={`px-4 py-2 text-white font-light text-base rounded-md transition-colors ${
                  activeTab === "users"
                    ? "bg-[#d4a017]"
                    : "bg-[#333] hover:bg-[#555]"
                }`}
              >
                Users
              </button>
            )}
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-white font-light text-base rounded-md bg-[#f44336] hover:bg-[#da190b] transition-colors"
            >
              Logout
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-4 md:p-8 md:ml-[200px] overflow-auto">
          <div className="bg-[#1a1a1a] rounded-lg">
            {/* Use key={false} to prevent components from unmounting when switching tabs */}

            {activeTab === "venues" && (
              <VenueTab key="venues-tab" setActiveTab={setActiveTab} />
            )}
            {
              <div
                style={{ display: activeTab === "events" ? "block" : "none" }}
              >
                <EventTab key="events-tab" />
              </div>
            }
            {
              <div
                style={{ display: activeTab === "cities" ? "block" : "none" }}
              >
                <CityTab key="cities-tab" />
              </div>
            }
            {
              <div
                style={{
                  display: activeTab === "performers" ? "block" : "none",
                }}
              >
                <PerformerTab key="performers-tab" />
              </div>
            }
            {
              <div
                style={{
                  display: activeTab === "composers" ? "block" : "none",
                }}
              >
                <ComposerTab key="composers-tab" />
              </div>
            }
            {
              <div
                style={{ display: activeTab === "hotels" ? "block" : "none" }}
              >
                <HotelTab key="hotels-tab" setActiveTab={setActiveTab} />
              </div>
            }
            {
              <div
                style={{ display: activeTab === "imports" ? "block" : "none" }}
              >
                <ImportTab key="imports-tab" />
              </div>
            }
            {
              <div
                style={{
                  display: activeTab === "information" ? "block" : "none",
                }}
              >
                <InformationTab key="information-tab" />
              </div>
            }
            {
              <div
                style={{
                  display: activeTab === "newsletter" ? "block" : "none",
                }}
              >
                <NewsletterTab key="newsletter-tab" />
              </div>
            }
            {userRole === "superadmin" && (
              <div
                style={{ display: activeTab === "users" ? "block" : "none" }}
              >
                <AdminUserTab key="users-tab" />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;
